// Typed fetch client. Cookies are httpOnly so we just need credentials:include.

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, text || res.statusText);
  }
  return res.json() as Promise<T>;
}

export type Setup = Record<string, unknown> & {
  symbol: string;
  timeframe: string;
  direction: "bull" | "bear";
  score: number;
  origin: string;
  setup_id?: string | null;
  tr_end_date?: string;
  score_ml?: number | null;
  expected_bars_to_breakout?: number | null;
  p_breakout_within_3?: number | null;
  p_breakout_within_10?: number | null;
  hazard_curve?: number[] | null;
  hazard_curve_horizons?: number[] | null;
};

export type Facets = {
  symbols: string[];
  timeframes: string[];
  directions: string[];
  origins: string[];
  max_score: number;
};

export type SetupsResponse = {
  setups: Setup[];
  facets: Facets;
  total: number;
  offset: number;
  limit: number;
};

export type HmmStateSummary = {
  raw_index: number;
  name: string;
  means: number[];
  stddevs: number[];
  stay_probability: number;
  expected_dwell_bars: number;
};

export type HmmSummary = {
  timeframe: string;
  n_states: number;
  feature_names: string[];
  states: HmmStateSummary[];
  transition_matrix: number[][];
  start_probabilities: number[];
  covariance_type: string | null;
};

export type GbmSummary = {
  timeframe: string;
  trained_at?: string | null;
  n_rows?: number | null;
  target_source?: string | null;
  hand_labels_used?: number | null;
  best_iteration?: number | null;
  rmse_holdout?: number | null;
  feature_importances?: { name: string; gain: number }[];
};

export type RsfSummary = {
  timeframe: string;
  trained_at?: string | null;
  n_rows?: number | null;
  n_events?: number | null;
  c_index_in_sample?: number | null;
  features?: string[];
};

export type EvalCalibrationBin = {
  decile: number;
  horizon?: number;
  n: number;
  p_pred_mean: number;
  p_emp: number;
};

export type EvalDecileLift = {
  decile: number;
  n: number;
  mean_score: number;
  mean_outcome: number;
};

export type RsfEvalSummary = {
  n_train: number;
  n_test: number;
  n_events_train: number;
  n_events_test: number;
  c_index_test: number;
  integrated_brier: Record<string, number | string>;
  calibration: EvalCalibrationBin[];
};

export type GbmEvalSummary = {
  n_train: number;
  n_test: number;
  spearman: number;
  auroc: number;
  decile_lift: EvalDecileLift[];
};

export type EvalSummary = {
  timeframe: string;
  cutoff_date: string;
  embargo_bars: number;
  n_rows_total: number;
  feature_count: number;
  rsf: RsfEvalSummary | null;
  gbm: GbmEvalSummary | null;
  notes?: string[];
  persisted_at?: string;
};

export type ModelSummary = {
  timeframe: string;
  hmm: HmmSummary | null;
  gbm: GbmSummary | null;
  rsf: RsfSummary | null;
  eval: EvalSummary | null;
};

export type Bars = {
  symbol: string;
  timeframe: string;
  t: string[];
  o: (number | null)[];
  h: (number | null)[];
  l: (number | null)[];
  c: (number | null)[];
  v: (number | null)[];
};

export const api = {
  health: () =>
    request<{ ok: boolean; auth_required: boolean }>("/api/health"),
  login: (password: string) =>
    request<{ ok: boolean }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  logout: () => request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  setups: (params: Record<string, string | number | boolean | undefined>) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === "" || v === false) continue;
      q.set(k, String(v));
    }
    const qs = q.toString();
    return request<SetupsResponse>(`/api/setups${qs ? `?${qs}` : ""}`);
  },
  bars: (symbol: string, tf: string, start?: string, end?: string) => {
    const q = new URLSearchParams();
    if (start) q.set("start", start);
    if (end) q.set("end", end);
    const qs = q.toString();
    return request<Bars>(
      `/api/bars/${encodeURIComponent(symbol)}/${encodeURIComponent(tf)}${qs ? `?${qs}` : ""}`,
    );
  },
  universe: () =>
    request<{ universe: Record<string, string[]> }>("/api/universe"),
  searchSymbols: (q: string, limit = 20) => {
    const params = new URLSearchParams({ q, limit: String(limit) });
    return request<{ results: { symbol: string; name: string }[] }>(
      `/api/symbols/search?${params.toString()}`,
    );
  },
  lastBars: () =>
    request<{
      last_bars: { symbol: string; timeframe: string; last_bar_date: string }[];
    }>("/api/last-bars"),
  previewSetup: (body: {
    symbol: string;
    tf: string;
    anchors: [number, number, number];
    direction?: "bull" | "bear";
  }) =>
    request<{
      ok: boolean;
      direction: "bull" | "bear";
      direction_inferred: boolean;
      setup: Setup;
    }>("/api/setups/preview", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  createSetup: (body: {
    symbol: string;
    tf: string;
    anchors: [number, number, number];
    direction?: "bull" | "bear";
  }) =>
    request<{
      ok: boolean;
      direction: "bull" | "bear";
      setup: Setup;
    }>("/api/setups", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteSetup: (params: { symbol: string; tf: string; setup_id: string }) => {
    const q = new URLSearchParams({
      symbol: params.symbol,
      tf: params.tf,
      sid: params.setup_id,
    });
    return request<{ ok: boolean }>(`/api/setups?${q.toString()}`, {
      method: "DELETE",
    });
  },
  models: () =>
    request<{ timeframes: ModelSummary[] }>(`/api/models`),
  labels: (params: { symbol?: string; tf?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.symbol) q.set("symbol", params.symbol);
    if (params.tf) q.set("tf", params.tf);
    const qs = q.toString();
    return request<{ labels: Record<string, unknown>[] }>(
      `/api/labels${qs ? `?${qs}` : ""}`,
    );
  },
  createLabel: (body: {
    symbol: string;
    tf: string;
    setup_id: string;
    label: "good" | "bad" | "borderline";
    notes?: string;
  }) =>
    request<{ ok: boolean }>("/api/labels", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
